import React, { useState, useEffect } from 'react';
import { HardwareTypeCreate } from '../../services/typesService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { useI18n } from '../../contexts/I18nContext';
import './AddHardwareTypeModal.css';

interface AddHardwareTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  onCreate: (data: HardwareTypeCreate) => Promise<void>;
}

interface FormErrors {
  [key: string]: string;
}

export const AddHardwareTypeModal: React.FC<AddHardwareTypeModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  onCreate
}) => {
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [formData, setFormData] = useState<HardwareTypeCreate>({
    code: '',
    name: '',
    description: '',
    reboot_payload: '',
    client_id: ''
  });

  // Fetch clients when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchClients = async () => {
        setLoadingClients(true);
        try {
          const fetchedClients = await clientService.fetchActiveClients();
          setClients(fetchedClients);
        } catch (error) {
          console.error('Failed to fetch clients:', error);
        } finally {
          setLoadingClients(false);
        }
      };
      fetchClients();
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.client_id) {
      newErrors.client_id = 'Client selection is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Device type code is required';
    } else if (!/^[A-Z0-9]{2,10}$/.test(formData.code)) {
      newErrors.code = 'Code must be 2-10 uppercase letters/numbers';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Commercial name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof HardwareTypeCreate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onCreate(formData);
      onCreated();
      resetForm();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail
        || (error instanceof Error ? error.message : 'Failed to create hardware type');
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ code: '', name: '', description: '', reboot_payload: '', client_id: '' });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="add-hardware-type-modal" data-testid="modal-add-hardware-type" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <div className="header-icon">
              <span>+</span>
            </div>
            <h2>{t('deviceTypes.addHardwareType')}</h2>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Banner */}
        {errors.submit && (
          <div className="error-banner">
            <div className="error-content">
              <span className="error-icon">!</span>
              <span>{errors.submit}</span>
            </div>
            <button onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            {/* Section: Client Selection */}
            <div className="form-section client-section">
              <h3 className="section-title">{t('clients.client')}</h3>
              <p className="section-description">
                Hardware types are client-specific. Select the client this hardware type belongs to.
              </p>

              <div className="form-fields">
                <div className="form-field full-width">
                  <label className="required">{t('clients.client')}</label>
                  <select
                    value={formData.client_id}
                    data-testid="field-client-id"
                    onChange={(e) => handleInputChange('client_id', e.target.value)}
                    className={`field-select ${errors.client_id ? 'error' : ''}`}
                    disabled={isSubmitting || loadingClients}
                  >
                    <option value="">
                      {loadingClients ? 'Loading clients...' : 'Select a client'}
                    </option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  <span className="field-hint">{t('deviceTypes.clientOrganizationHelp')}</span>
                  {errors.client_id && <span className="field-error">{errors.client_id}</span>}
                </div>
              </div>
            </div>

            {/* Section: Basic Information */}
            <div className="form-section">
              <h3 className="section-title">{t('devices.basicInformation')}</h3>
              <p className="section-description">
                Define the hardware type identifier and commercial name used across the system.
              </p>

              <div className="form-fields">
                <div className="form-field">
                  <label className="required">{t('deviceTypes.deviceTypeCode')}</label>
                  <input
                    type="text"
                    value={formData.code}
                    data-testid="field-hardware-type-code"
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    className={`field-input ${errors.code ? 'error' : ''}`}
                    placeholder="e.g., PGA, IOTL, TMS2"
                    maxLength={10}
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">2-10 uppercase letters or numbers</span>
                  {errors.code && <span className="field-error">{errors.code}</span>}
                </div>

                <div className="form-field">
                  <label className="required">{t('deviceTypes.commercialName')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    data-testid="field-hardware-type-name"
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`field-input ${errors.name ? 'error' : ''}`}
                    placeholder="e.g., PGA Gateway, IoT Logger"
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">{t('deviceTypes.commercialNameHelp')}</span>
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
              </div>
            </div>

            {/* Section: Additional Details */}
            <div className="form-section">
              <h3 className="section-title">{t('deviceTypes.additionalDetails')}</h3>
              <p className="section-description">
                Optional description to help identify this hardware type.
              </p>

              <div className="form-fields">
                <div className="form-field full-width">
                  <label>{t('common.description')}</label>
                  <textarea
                    value={formData.description || ''}
                    data-testid="field-hardware-type-description"
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="field-textarea"
                    placeholder={t('deviceTypes.descriptionPlaceholder')}
                    rows={3}
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">Optional - describe the hardware capabilities or use case</span>
                </div>

                <div className="form-field full-width">
                  <label>{t('deviceTypes.rebootPayload')}</label>
                  <input
                    type="text"
                    value={formData.reboot_payload || ''}
                    data-testid="field-hardware-type-reboot-payload"
                    onChange={(e) => handleInputChange('reboot_payload', e.target.value)}
                    className="field-input"
                    placeholder="e.g., 97# or 7#"
                    maxLength={16}
                    disabled={isSubmitting}
                  />
                  <span className="field-hint">{t('deviceTypes.rebootPayloadHelp')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <div className="footer-left">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary"
                data-testid="btn-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
            <div className="footer-right">
              <button
                type="submit"
                className="btn btn-primary"
                data-testid="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Creating...
                  </>
                ) : (
                  'Create Hardware Type'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
